import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { policiesAPI, slabsAPI } from '../services/api';

const POLICY_TYPES = [
  { value: 'employee', label: 'Employee' },
  { value: 'parent', label: 'Parent' },
  { value: 'top_up', label: 'Top-up' },
];

export default function Slabs() {
  const [searchParams] = useSearchParams();
  const [policies, setPolicies] = useState([]);
  const [policyId, setPolicyId] = useState('');
  const [slabs, setSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    policy_type: 'employee', age_min: '', age_max: '', annual_premium: '',
    risk_category: '', effective_from: '', rider_critical_illness: 0, rider_accident_cover: 0,
  });

  useEffect(() => {
    policiesAPI.list().then(({ data }) => {
      setPolicies(data);
      const fromQuery = searchParams.get('policy');
      if (fromQuery && data.some((p) => String(p.id) === fromQuery)) {
        setPolicyId(fromQuery);
      } else if (data[0]) {
        setPolicyId(String(data[0].id));
      }
    }).finally(() => setLoading(false));
  }, [searchParams]);

  useEffect(() => {
    if (!policyId) return;
    slabsAPI.byPolicy(policyId).then(({ data }) => setSlabs(data));
  }, [policyId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await slabsAPI.create({
      policy_id: parseInt(policyId, 10),
      ...form,
      age_min: parseInt(form.age_min, 10),
      age_max: parseInt(form.age_max, 10),
      annual_premium: parseFloat(form.annual_premium),
      rider_critical_illness: parseFloat(form.rider_critical_illness) || 0,
      rider_accident_cover: parseFloat(form.rider_accident_cover) || 0,
    });
    const { data } = await slabsAPI.byPolicy(policyId);
    setSlabs(data);
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Premium Age Slab Master</h1>
        <p className="text-slate-500">M2 — configurable rate cards per policy with riders &amp; effective dating</p>
      </div>

      <div className="card">
        <label className="label">Select Policy</label>
        <select className="input-field max-w-md" value={policyId} onChange={(e) => setPolicyId(e.target.value)}>
          {policies.map((p) => (
            <option key={p.id} value={p.id}>{p.company_name} — {p.policy_number}</option>
          ))}
        </select>
      </div>

      <form onSubmit={handleSubmit} className="card space-y-4">
        <h3 className="font-semibold">Add Age Slab</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          <div>
            <label className="label">Policy Type</label>
            <select className="input-field" value={form.policy_type} onChange={(e) => setForm({ ...form, policy_type: e.target.value })}>
              {POLICY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div><label className="label">Age Min</label><input type="number" className="input-field" required value={form.age_min} onChange={(e) => setForm({ ...form, age_min: e.target.value })} /></div>
          <div><label className="label">Age Max</label><input type="number" className="input-field" required value={form.age_max} onChange={(e) => setForm({ ...form, age_max: e.target.value })} /></div>
          <div><label className="label">Annual Premium (INR)</label><input type="number" className="input-field" required value={form.annual_premium} onChange={(e) => setForm({ ...form, annual_premium: e.target.value })} /></div>
          <div><label className="label">Risk Category</label><input className="input-field" value={form.risk_category} onChange={(e) => setForm({ ...form, risk_category: e.target.value })} /></div>
          <div><label className="label">Effective From</label><input type="date" className="input-field" required value={form.effective_from} onChange={(e) => setForm({ ...form, effective_from: e.target.value })} /></div>
          <div><label className="label">Critical Illness Rider</label><input type="number" className="input-field" value={form.rider_critical_illness} onChange={(e) => setForm({ ...form, rider_critical_illness: e.target.value })} /></div>
          <div><label className="label">Accident Cover Rider</label><input type="number" className="input-field" value={form.rider_accident_cover} onChange={(e) => setForm({ ...form, rider_accident_cover: e.target.value })} /></div>
        </div>
        <p className="text-xs text-slate-500">GST at 18% is auto-calculated on premiums during enrollment (M2/M6).</p>
        <button type="submit" className="btn-primary">Add Slab</button>
      </form>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="pb-2">Type</th><th className="pb-2">Age Band</th><th className="pb-2">Premium</th>
              <th className="pb-2">Risk</th><th className="pb-2">Riders</th><th className="pb-2">Effective</th>
            </tr>
          </thead>
          <tbody>
            {slabs.map((s) => (
              <tr key={s.id} className="border-b border-slate-100">
                <td className="py-2 capitalize">{s.policy_type}</td>
                <td>{s.age_min} – {s.age_max}</td>
                <td>₹{Number(s.annual_premium).toLocaleString('en-IN')}</td>
                <td>{s.risk_category}</td>
                <td>CI: ₹{s.rider_critical_illness || 0} / PA: ₹{s.rider_accident_cover || 0}</td>
                <td>{s.effective_from}{s.effective_to ? ` → ${s.effective_to}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
